import { tick } from "@simplybusiness/icons";
import { Icon, Link, Text } from "@simplybusiness/mobius";
import { useSnowplowContext } from "@simplybusiness/services";
import classNames from "classnames/dedupe";
import type { ProgressStepsItemProps } from "./types";

export const ProgressStepsItem = ({
  ref,
  ...props
}: ProgressStepsItemProps) => {
  const {
    isComplete,
    url,
    to,
    name,
    isActive,
    index,
    className,
    activeName,
    activeIndex,
    ...otherProps
  } = props;

  const { snowplow } = useSnowplowContext();
  const classes = classNames(
    "mobius",
    "mobius-progress-steps__item",
    {
      "--is-active": isActive,
      "--is-complete": isComplete,
      "--is-incomplete": !isComplete && !isActive,
    },
    className,
  );
  const linkClasses = classNames(
    "mobius-progress-steps__item-link",
    "mobius-link",
  );
  const shapeClasses = "mobius-progress-steps__item-shape";
  const nameClasses = "mobius-progress-steps__item-name";
  const nameInnerClasses = "mobius-progress-steps__item-name-inner";
  const ariaLabel = `Step ${index}${name ? `, ${name}` : ""}${isComplete ? ", Complete" : ""}`;
  const handleProgressStepItemClick = () => {
    snowplow?.trigger("progressbarClicked", {
      fromStepIndex: activeIndex,
      fromStepName: activeName,
      stepIndex: index,
      stepName: name,
    });
    return false;
  };

  return (
    <li
      ref={ref}
      aria-label={ariaLabel}
      aria-current={isActive ? "step" : undefined}
      {...otherProps}
      className={classes}
    >
      {isComplete ? (
        <Link
          className={linkClasses}
          href={url}
          to={to}
          onClick={handleProgressStepItemClick}
        >
          <div className={shapeClasses}>
            <Icon
              icon={tick}
              className="mobius-progress-steps__item-icon"
              size="md"
              title="Step completed"
            />
          </div>
          <Text variant="small" className={nameClasses}>
            {name}
          </Text>
        </Link>
      ) : (
        <>
          <div className={shapeClasses}>
            <span className="mobius-progress-steps__item-number">{index}</span>
          </div>
          <Text variant="small" className={nameClasses}>
            <span className={nameInnerClasses}>{name}</span>
          </Text>
        </>
      )}
    </li>
  );
};

ProgressStepsItem.displayName = "ProgressStepsItem";
